import React, { useEffect, useMemo, useRef } from "react";
import { useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { FiBarChart2, FiBriefcase, FiCreditCard, FiGrid, FiShield, FiUser, FiUsers } from "react-icons/fi";
import { OrgPageHeader } from "./components/organisation/OrgModal";
import ProfileSettings from "./components/settings/ProfileSettings";
import OrganisationSettingsPage from "./OrganisationSettingsPage";
import OrganisationMembersPage from "./OrganisationMembersPage";
import SuperAdminPage from "./SuperAdminPage";
import PlanSettings from "./components/settings/PlanSettings";
import UsageSettings from "./components/settings/UsageSettings";
import { can, homeOrganisation, readSession } from "./utils/organisationApi";

export const SETTINGS_TAB = {
  PROFILE: "profile",
  ORGANISATION: "organisation",
  TEAM: "team",
  PLAN: "plan",
  USAGE: "usage",
  ALL_ORGANISATIONS: "all-organisations",
};

// Tabs this person may see (Profile and Settings spec, section 3)
export const settingsTabsFor = (session) =>
  [
    { id: SETTINGS_TAB.PROFILE, label: "Profile", icon: FiUser, visible: true },
    { id: SETTINGS_TAB.ORGANISATION, label: "My organisation", icon: FiBriefcase, visible: can(session, "viewOrganisation") },
    { id: SETTINGS_TAB.TEAM, label: "Team", icon: FiUsers, visible: can(session, "viewTeam") },
    // The Super Admin console: every organisation on the platform
    { id: SETTINGS_TAB.ALL_ORGANISATIONS, label: "All organisations", icon: FiGrid, visible: Boolean(session.isSuperAdmin), special: true },
    // Usage is its own tab, shown to the same people as Plan & billing
    {
      id: SETTINGS_TAB.USAGE,
      label: "Usage",
      icon: FiBarChart2,
      visible: session.isSuperAdmin ? Boolean(session.organisationId || homeOrganisation(session)) : can(session, "manageBilling"),
    },
    // Plan & billing is the last tab. A Super Admin gets it inside any organisation, or for their own when outside all.
    {
      id: SETTINGS_TAB.PLAN,
      label: "Plan & billing",
      icon: FiCreditCard,
      visible: session.isSuperAdmin ? Boolean(session.organisationId || homeOrganisation(session)) : can(session, "manageBilling"),
    },
  ].filter((tab) => tab.visible);

const SettingsPage = () => {
  const theme = useTheme();
  const session = useMemo(() => readSession(), []);
  const tabs = useMemo(() => settingsTabsFor(session), [session]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabRefs = useRef({});

  const requested = searchParams.get("tab");
  const activeTab = tabs.some((tab) => tab.id === requested) ? requested : SETTINGS_TAB.PROFILE;

  // A missing or hidden tab opens Profile
  useEffect(() => {
    if (requested !== activeTab) setSearchParams({ tab: activeTab }, { replace: true });
  }, [requested, activeTab, setSearchParams]);

  const selectTab = (id) => {
    if (id !== activeTab) setSearchParams({ tab: id });
  };

  const handleKeyDown = (e) => {
    const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
    let index = tabs.findIndex((tab) => tab.id === activeTab);
    if (e.key in keys) index = (index + keys[e.key] + tabs.length) % tabs.length;
    else if (e.key === "Home") index = 0;
    else if (e.key === "End") index = tabs.length - 1;
    else return;
    e.preventDefault();
    const next = tabs[index];
    selectTab(next.id);
    tabRefs.current[next.id]?.focus();
  };

  // In a support session the Profile tab is the organisation's, not the support person's own
  const profileIsOrganisation = Boolean(session.isSupportSession);

  const panelFor = (id) => {
    if (id === SETTINGS_TAB.PROFILE && profileIsOrganisation) return <OrganisationSettingsPage />;
    if (id === SETTINGS_TAB.ORGANISATION) return <OrganisationSettingsPage />;
    if (id === SETTINGS_TAB.TEAM) return <OrganisationMembersPage />;
    if (id === SETTINGS_TAB.PLAN) return <PlanSettings />;
    if (id === SETTINGS_TAB.USAGE) return <UsageSettings />;
    if (id === SETTINGS_TAB.ALL_ORGANISATIONS) return <SuperAdminPage />;
    return null;
  };

  return (
    <div
      className="flex flex-col w-full h-full overflow-hidden"
      style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}
    >
      <OrgPageHeader title="Settings" />
      <div className="flex flex-1 min-h-0 max-md:flex-col">
        <div
          role="tablist"
          aria-label="Settings"
          aria-orientation="vertical"
          onKeyDown={handleKeyDown}
          className="flex shrink-0 gap-1 p-4 md:w-60 md:flex-col md:border-r max-md:border-b max-md:overflow-x-auto"
          style={{ backgroundColor: theme.palette.primary.main, borderColor: theme.palette.secondary.v4 }}
        >
          {tabs.map(({ id, label, icon: Icon, special }) => {
            const active = id === activeTab;
            return (
              <button
                key={id}
                ref={(el) => {
                  tabRefs.current[id] = el;
                }}
                id={`settings-tab-${id}`}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={`settings-panel-${id}`}
                tabIndex={active ? 0 : -1}
                onClick={() => selectTab(id)}
                className={`flex items-center gap-3 shrink-0 px-3 py-2.5 rounded-md text-sm font-medium text-left whitespace-nowrap transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black ${active ? "" : "hover:bg-black/[0.04]"}`}
                style={{
                  backgroundColor: active ? theme.palette.info.main : "transparent",
                  color: active ? "#ffffff" : theme.palette.text.main,
                }}
              >
                <Icon className="text-base shrink-0" aria-hidden="true" />
                {label}
                {special && (
                  <>
                    <span
                      title="Super Admin only"
                      className={`ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${active ? "bg-white text-violet-700" : "bg-violet-100 text-violet-700"}`}
                    >
                      <FiShield className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                    <span className="sr-only">(Super Admin only)</span>
                  </>
                )}
              </button>
            );
          })}
        </div>

        {/* Profile stays mounted so unsaved edits survive a trip to another tab */}
        {!profileIsOrganisation && (
          <div
            id={`settings-panel-${SETTINGS_TAB.PROFILE}`}
            role="tabpanel"
            aria-labelledby={`settings-tab-${SETTINGS_TAB.PROFILE}`}
            hidden={activeTab !== SETTINGS_TAB.PROFILE}
            className="flex-1 min-w-0 min-h-0"
          >
            <ProfileSettings />
          </div>
        )}
        {(profileIsOrganisation || activeTab !== SETTINGS_TAB.PROFILE) && (
          <div
            id={`settings-panel-${activeTab}`}
            role="tabpanel"
            aria-labelledby={`settings-tab-${activeTab}`}
            className="flex-1 min-w-0 min-h-0"
          >
            {panelFor(activeTab)}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
